import * as R from "ramda";
import colors from "../../../../styles/colors";
import {
  courseComponentViews,
  courseInstructionPartViews,
  courseInstructionViews,
  courseUnitViews,
  fullCourseViews,
  gridColumnView,
} from "../../../../utils/courseViews";
import { PreferredLocale } from "../../../../utils/locales";
import { IPageLockSettings } from "../../../pages/CourseAuthor/CourseInstruction/Parts/PageLockSettings";
import { componentIndex } from "../../../pages/CourseAuthor/PreviewTab/util";
import {
  BackgroundAblePart,
  ChoiceExerciseWrapping,
  CourseComponentTag,
  CourseComponentType,
  CourseInstructionType,
  CoursePlayer,
  CourseUnitView,
  CourseView,
  DocBorderThicknessPreset,
  DocMarginPreset,
  IAssessment,
  IChoiceExercise,
  IChoiceExerciseItem,
  IClozeExercise,
  IClozeGap,
  IClozeGapLocalizedAnswer,
  ICourseComponent,
  ICourseContentSettings,
  ICourseExerciseWrapping,
  ICourseInstruction,
  ICourseInstructionContentPart,
  ICourseLocalizedText,
  ICoursePublication,
  ICourseSet,
  ICourseStyle,
  ICourseUnit,
  ICourseVoiceOver,
  IDividerPart,
  IDividerSettings,
  IDocumentPart,
  IDocumentSettings,
  IFullCourse,
  IGridPart,
  IImagePart,
  IImageSettings,
  IListWithOptionsExercise,
  IListWithoutOptionsExercise,
  IMultipleChoiceExercise,
  isAssessment,
  ISingleChoiceExercise,
  ISpacerPart,
  ISpacerSettings,
  ITeachingContent,
  IUnorderedListExercise,
  NullablePartPosition,
  QuestionDrawingPreference,
} from "../../types";
import {
  IClientPatch,
  IPatch,
  IPatchAction,
  IPatchAddAction,
  IPatchDeleteAction,
  IPatchInsertAction,
  IPatchMoveAction,
  IPatchPayload,
  IPatchSubstAction,
  PatchType,
} from "./index";
import {
  findComponent,
  findLastChildComponentIndex,
  findNextId,
  findPartPosition,
  findTitlePosition,
  getPositionedValues,
} from "./util";

const DEFAULT_MARGIN = 50;

export const DEFAULT_DOCUMENT_SETTINGS: IDocumentSettings["settings"] = {
  backgroundColor: colors.white,
  backgroundImageId: "",
  backgroundImageName: "",
  backgroundImageSize: 0,
  borderColor: colors.black,
  borderStyle: "solid",
  borderThickness: DocBorderThicknessPreset.Medium,
  fileDocumentId: "",
  fileName: "",
  fileSize: 0,
  hasBackground: false,
  margin: DocMarginPreset.Medium,
  size: "medium",
  style: "no-border",
};

export function patchPayloadCreator(
  baseVersionId: number,
  clientPatch: IClientPatch<IPatchAction> | Array<IClientPatch<IPatchAction>>
): IPatchPayload;
export function patchPayloadCreator(
  baseVersionId: number,
  ...clientPatches: Array<IClientPatch<IPatchAction>>
): IPatchPayload {
  const patches: Array<IClientPatch<IPatchAction>> = R.flatten(clientPatches);
  return {
    baseVersionId,
    clientPatches: patches.map((patch, i) => ({
      ...patch,
      versionId: baseVersionId + i + 1,
    })),
  };
}

export function substPatch<T>(x: T, y: T, position: string): IPatch<IPatchSubstAction<T>> {
  return {
    action: {
      type: PatchType.subst,
      x,
      y,
    },
    position,
  };
}

export function addPatch<T>(item: T, at: number, position: string): IPatch<IPatchAddAction<T>> {
  return {
    action: {
      at,
      item,
      type: PatchType.add,
    },
    position,
  };
}

export function addCourseInstructionPartPatch(
  course: IFullCourse,
  userId: string,
  instructionSlide: ICourseInstruction | IAssessment,
  unitPath: CourseUnitView[],
  type: CourseComponentType,
  index?: number,
  properties: object = {}
): IClientPatch<IPatchAddAction<ICourseInstructionContentPart>> {
  const { position, nextId, insertedElementIndex } = getPositionedValues(course, instructionSlide, unitPath, index);

  if (R.isNil(nextId)) {
    throw new Error(`Could not find next id for instruction content at ${position}`);
  }

  const item: ICourseInstructionContentPart = createNewInstructionPart(type, nextId, properties);

  return {
    ...createPatchBase(userId, course.versionId + 1),
    patch: addPatch(item, insertedElementIndex === -1 ? 0 : insertedElementIndex, position),
  };
}

export function copyCourseInstructionPartPatch(
  course: IFullCourse,
  userId: string,
  instructionSlide: ICourseInstruction | IAssessment,
  unitPath: CourseUnitView[],
  part: ICourseInstructionContentPart,
  index?: number
): IClientPatch<IPatchAddAction<ICourseInstructionContentPart>> {
  const { position, nextId, insertedElementIndex } = getPositionedValues(course, instructionSlide, unitPath, index);

  if (R.isNil(nextId)) {
    throw new Error(`Could not find next id for instruction content at ${position}`);
  }

  const item: ICourseInstructionContentPart = { ...part, id: nextId };

  return {
    ...createPatchBase(userId, course.versionId + 1),
    patch: addPatch(item, insertedElementIndex === -1 ? 0 : insertedElementIndex, position),
  };
}

export function copyGridPartPatch(
  course: IFullCourse,
  userId: string,
  instructionSlide: ICourseInstruction | IAssessment,
  unitPath: CourseUnitView[],
  part: ICourseInstructionContentPart,
  id: number,
  index?: number
): IClientPatch<IPatchAddAction<ICourseInstructionContentPart>> {
  const { position } = getPositionedValues(course, instructionSlide, unitPath, index);

  const item: ICourseInstructionContentPart = { ...part, id };

  return {
    ...createPatchBase(userId, course.versionId + 1),
    patch: addPatch(item, id, position),
  };
}

export const substLeafCourseInstructionColumnPatch = (
  course: IFullCourse,
  userId: string,
  instructionSlide: ICourseInstruction,
  unitPath: CourseUnitView[],
  part: IGridPart,
  columnIndex: number,
  newColumnValue: NullablePartPosition
): IClientPatch<IPatchSubstAction<NullablePartPosition>> => {
  const partPosition = findPartPosition(course, unitPath, instructionSlide, part);
  const position = `${partPosition}.columns.${columnIndex}`;
  const currentColumnValue = gridColumnView(part, columnIndex);

  return {
    ...createPatchBase(userId, course.versionId + 1),
    patch: substPatch(currentColumnValue, newColumnValue, position),
  };
};

export function deleteCourseInstructionPartPatch(
  course: IFullCourse,
  userId: string,
  instructionSlide: ICourseInstruction | IAssessment,
  unitPath: CourseUnitView[],
  part: ICourseInstructionContentPart
): IClientPatch<IPatchSubstAction<boolean>> {
  let position: string;
  if (isAssessment(instructionSlide)) {
    position = `${findComponent(course, unitPath)}.test.questionBank.${part.id}.deleted`;
  } else {
    position = `${findPartPosition(course, unitPath, instructionSlide, part)}.deleted`;
  }

  return {
    ...createPatchBase(userId, course.versionId + 1),
    patch: substPatch(false, true, position),
  };
}

export function copyComponentPatch(
  course: IFullCourse,
  destinationPath: Array<CourseView<ICourseUnit, IFullCourse>>,
  originalComponent: ICourseComponent,
  userId: string,
  insertAt: number
): IClientPatch<IPatchAddAction<ICourseComponent>> {
  const destinationUnitView = R.last(destinationPath);
  const nextId = (destinationUnitView
    ? courseUnitViews.nextChildId(destinationUnitView(course))
    : fullCourseViews.nextChildId(course)) as number;

  const at: number =
    insertAt >= 0
      ? insertAt
      : destinationUnitView
      ? (courseUnitViews.components(destinationUnitView(course)) || []).length
      : (fullCourseViews.units(course) || []).length;

  const item = {
    ...originalComponent,
    id: nextId,
  };

  const position = `${findComponent(course, destinationPath)}.children`;

  return {
    ...createPatchBase(userId, course.versionId + 1),
    patch: addPatch(item, at, position),
  };
}

export function publicationPatch(
  course: IFullCourse,
  userId: string,
  revision: number
): IClientPatch<IPatchAddAction<ICoursePublication>> {
  const at = course.state.publications.elems.length;
  const id = course.state.publications.nextId;
  const item = {
    id,
    notes: "",
    revision,
  };
  return {
    ...createPatchBase(userId, course.versionId + 1),
    patch: addPatch(item, at, "publications"),
  };
}

export function republicationPatch(
  course: IFullCourse,
  userId: string,
  publicationId: number
): IClientPatch<IPatchSubstAction<boolean>> {
  return {
    ...createPatchBase(userId, course.versionId + 1),
    patch: substPatch(true, false, `publications.${publicationId}.deleted`),
  };
}

export function unpublicationPatch(
  course: IFullCourse,
  userId: string,
  publicationId: number
): IClientPatch<IPatchSubstAction<boolean>> {
  return {
    ...createPatchBase(userId, course.versionId + 1),
    patch: substPatch(false, true, `publications.${publicationId}.deleted`),
  };
}

export function toggleLockCoursePatch(course: IFullCourse, userId: string): IClientPatch<IPatchSubstAction<boolean>> {
  return {
    ...createPatchBase(userId, course.versionId + 1),
    patch: substPatch(course.state.locked, !course.state.locked, "locked"),
  };
}

export function moveComponentPatch(
  course: IFullCourse,
  unitPath: CourseUnitView[],
  component: ICourseComponent,
  userId: string,
  moveTo: number
): IClientPatch<IPatchMoveAction> {
  const position = findComponent(course, unitPath);
  const courseIndex = componentIndex(course, unitPath, component);
  const lastIndex = findLastChildComponentIndex(course, unitPath);

  if (!R.isNil(courseIndex) && courseIndex >= 0) {
    return {
      ...createPatchBase(userId, course.versionId + 1),
      patch: {
        action: {
          from: courseIndex,
          to: moveTo >= 0 ? Math.min(moveTo, lastIndex) : lastIndex,
          type: PatchType.move,
        },
        position: `${position}.children`,
      },
    };
  }

  throw new Error(`Component not found when attempting to move component`);
}

export function addComponentTitlePatch(
  course: IFullCourse,
  unitPath: CourseUnitView[],
  component: ICourseComponent,
  userId: string,
  locale: PreferredLocale,
  title: string
): IClientPatch<IPatchAddAction<ICourseLocalizedText>> {
  const position = findTitlePosition(course, locale, component, unitPath);
  const at = courseComponentViews.nextTitleId(component) as number;
  const item = {
    id: at,
    locale,
    value: {
      text: title,
    },
  };

  return {
    ...createPatchBase(userId, course.versionId + 1),
    patch: addPatch(item, at, position),
  };
}

export function addHTMLContentPatch(
  course: IFullCourse,
  unitPath: CourseUnitView[],
  comp: ICourseInstruction,
  part: ICourseInstructionContentPart,
  locale: PreferredLocale,
  content: string,
  userId: string
): IClientPatch<IPatchAddAction<ICourseLocalizedText>> {
  const partPosition = findPartPosition(course, unitPath, comp, part);
  const at = courseInstructionPartViews.nextMarkdownId(part) as number;
  const item = {
    id: at,
    locale,
    value: {
      text: content,
    },
  };

  return {
    ...createPatchBase(userId, course.versionId + 1),
    patch: addPatch(item, at, `${partPosition}.markdown`),
  };
}

export function editDividerContentPatch(
  course: IFullCourse,
  unitPath: CourseUnitView[],
  comp: ICourseInstruction,
  part: IDividerPart,
  content: IDividerSettings,
  userId: string
): IClientPatch<IPatchSubstAction<IDividerSettings["settings"]>> {
  const partPosition = findPartPosition(course, unitPath, comp, part);

  return {
    ...createPatchBase(userId, course.versionId + 1),
    patch: substPatch(part.content.settings, content.settings, `${partPosition}.settings`),
  };
}

export const editSpacerContentPatch = (
  course: IFullCourse,
  unitPath: CourseUnitView[],
  comp: ICourseInstruction,
  part: ISpacerPart,
  content: ISpacerSettings,
  userId: string
): IClientPatch<IPatchSubstAction<ISpacerSettings["settings"]>> => {
  const partPosition = findPartPosition(course, unitPath, comp, part);

  return {
    ...createPatchBase(userId, course.versionId + 1),
    patch: substPatch(part.content.settings, content.settings, `${partPosition}.settings`),
  };
};

export const changeBackgroundPatch = (
  course: IFullCourse,
  unitPath: CourseUnitView[],
  component: ICourseInstruction,
  part: BackgroundAblePart,
  imageId: Nullable<string>,
  userId: string
) => {
  const partPosition = findPartPosition(course, unitPath, component, part);

  return {
    ...createPatchBase(userId, course.versionId + 1),
    patch: substPatch(part.content.background ?? null, { imageId }, `${partPosition}.background`),
  };
};

export const editImageContentPatch = (
  course: IFullCourse,
  unitPath: CourseUnitView[],
  comp: ICourseInstruction,
  part: IImagePart,
  content: IImageSettings,
  userId: string
): IClientPatch<IPatchSubstAction<IImageSettings["settings"]>> => {
  const partPosition = findPartPosition(course, unitPath, comp, part);

  return {
    ...createPatchBase(userId, course.versionId + 1),
    patch: substPatch(part.content.settings, content.settings, `${partPosition}.settings`),
  };
};

export const editDocumentContentPatch = (
  course: IFullCourse,
  unitPath: CourseUnitView[],
  comp: ICourseInstruction,
  part: IDocumentPart,
  content: IDocumentPart["content"],
  userId: string
): IClientPatch<IPatchSubstAction<IDocumentSettings["settings"]>> => {
  const partPosition = findPartPosition(course, unitPath, comp, part);

  return {
    ...createPatchBase(userId, course.versionId + 1),
    patch: substPatch(part.content.settings, content.settings, `${partPosition}.settings`),
  };
};

export function modifyHTMLContentPatch(
  course: IFullCourse,
  unitPath: CourseUnitView[],
  comp: ICourseInstruction,
  part: ITeachingContent,
  locale: PreferredLocale,
  content: string,
  userId: string
): IClientPatch<IPatchSubstAction<string>> {
  const partPosition = findPartPosition(course, unitPath, comp, part);
  const elems: ICourseLocalizedText[] = R.pathOr([], ["content", "markdown", "elems"], part);
  const currentLocalizedText = R.find((t: ICourseLocalizedText) => t.locale === locale, elems);
  const currentContent = courseInstructionPartViews.localizedContent(part, locale) as string;

  if (R.isNil(currentContent) || R.isNil(currentLocalizedText)) {
    throw new Error(`Could not find existing HTML content for ${partPosition}`);
  }

  return {
    ...createPatchBase(userId, course.versionId + 1),
    patch: substPatch(currentContent, content, `${partPosition}.markdown.${currentLocalizedText.id}.value.text`),
  };
}

export function addVoiceOverPatch(
  course: IFullCourse,
  unitPath: CourseUnitView[],
  comp: ICourseInstruction,
  part: ICourseInstructionContentPart,
  locale: PreferredLocale,
  userId: string
): IClientPatch<IPatchAddAction<ICourseVoiceOver>> {
  const partPosition = findPartPosition(course, unitPath, comp, part);
  const at = courseInstructionPartViews.nextVoiceOverId(part) as number;
  const item: ICourseVoiceOver = {
    id: at,
    locale,
    value: {
      enabled: true,
      mediaId: null,
      startAt: null,
      stopAt: null,
    },
  };

  return {
    ...createPatchBase(userId, course.versionId + 1),
    patch: addPatch(item, at, `${partPosition}.voiceOver`),
  };
}

export function enableVoiceOverPatch(
  course: IFullCourse,
  unitPath: CourseUnitView[],
  comp: ICourseInstruction,
  part: ICourseInstructionContentPart,
  locale: PreferredLocale,
  userId: string
): IClientPatch<IPatchSubstAction<boolean>> {
  const partPosition = findPartPosition(course, unitPath, comp, part);
  const currentVoiceOver = courseInstructionPartViews.localizedVoiceOver(part, locale);

  if (R.isNil(currentVoiceOver)) {
    throw Error("Could not find current voiceOver data");
  }

  return {
    ...createPatchBase(userId, course.versionId + 1),
    patch: substPatch<boolean>(
      currentVoiceOver.value.enabled,
      !currentVoiceOver.value.enabled,
      `${partPosition}.voiceOver.${currentVoiceOver.id}.value.enabled`
    ),
  };
}

export function modifyVoiceOverMediaPatch(
  course: IFullCourse,
  unitPath: CourseUnitView[],
  comp: ICourseInstruction,
  part: ICourseInstructionContentPart,
  locale: PreferredLocale,
  mediaId: string,
  userId: string
): IClientPatch<IPatchSubstAction<Nullable<string>>> {
  const partPosition = findPartPosition(course, unitPath, comp, part);
  const currentVoiceOver = courseInstructionPartViews.localizedVoiceOver(part, locale);

  if (R.isNil(currentVoiceOver)) {
    throw Error("Could not find current voice over data");
  }

  return {
    ...createPatchBase(userId, course.versionId + 1),
    patch: substPatch<Nullable<string>>(
      currentVoiceOver.value.mediaId,
      mediaId,
      `${partPosition}.voiceOver.${currentVoiceOver.id}.value.mediaId`
    ),
  };
}

export function addVoiceOverTextPatch(
  course: IFullCourse,
  unitPath: CourseUnitView[],
  comp: ICourseInstruction,
  part: ICourseInstructionContentPart,
  locale: PreferredLocale,
  text: string,
  userId: string
): IClientPatch<IPatchAddAction<ICourseLocalizedText>> {
  const partPosition = findPartPosition(course, unitPath, comp, part);
  const at = courseInstructionPartViews.nextVoiceOverTextId(part) as number;
  const item: ICourseLocalizedText = {
    id: at,
    locale,
    value: {
      text,
    },
  };

  return {
    ...createPatchBase(userId, course.versionId + 1),
    patch: addPatch(item, at, `${partPosition}.voiceOverText`),
  };
}

export function modifyVoiceOverTextPatch(
  course: IFullCourse,
  unitPath: CourseUnitView[],
  comp: ICourseInstruction,
  part: ITeachingContent,
  locale: PreferredLocale,
  text: string,
  userId: string
): IClientPatch<IPatchSubstAction<string>> {
  const partPosition = findPartPosition(course, unitPath, comp, part);
  const currentVoiceOverText: ICourseLocalizedText | undefined = R.find(
    (t: ICourseLocalizedText) => t.locale === locale,
    part.content.voiceOverText.elems
  );
  const currentVoiceOverTextValue = courseInstructionPartViews.localizedVoiceOverText(part, locale);

  if (R.isNil(currentVoiceOverTextValue) || R.isNil(currentVoiceOverText)) {
    throw Error("Could not find current voiceOverText data");
  }

  return {
    ...createPatchBase(userId, course.versionId + 1),
    patch: substPatch(
      currentVoiceOverTextValue,
      text,
      `${partPosition}.voiceOverText.${currentVoiceOverText.id}.value.text`
    ),
  };
}

export function modifyVoiceOverTimePatch(
  course: IFullCourse,
  unitPath: CourseUnitView[],
  comp: ICourseInstruction,
  part: ICourseInstructionContentPart,
  locale: PreferredLocale,
  startAt: Nullable<number>,
  stopAt: Nullable<number>,
  userId: string
): Array<IClientPatch<IPatchSubstAction<Nullable<number>>>> {
  const partPosition = findPartPosition(course, unitPath, comp, part);
  const currentVoiceOver = courseInstructionPartViews.localizedVoiceOver(part, locale);

  if (R.isNil(currentVoiceOver)) {
    throw Error("Could not find current voice over data");
  }

  const patches = [];

  if (startAt !== currentVoiceOver.value.startAt) {
    patches.push({
      ...createPatchBase(userId, course.versionId + 1),
      patch: substPatch<Nullable<number>>(
        currentVoiceOver.value.startAt,
        startAt,
        `${partPosition}.voiceOver.${currentVoiceOver.id}.value.startAt`
      ),
    });
  }

  if (stopAt !== currentVoiceOver.value.stopAt) {
    patches.push({
      ...createPatchBase(userId, course.versionId + 1 + patches.length),
      patch: substPatch<Nullable<number>>(
        currentVoiceOver.value.stopAt,
        stopAt,
        `${partPosition}.voiceOver.${currentVoiceOver.id}.value.stopAt`
      ),
    });
  }

  return patches;
}

export function addCourseDescriptionPatch(
  course: IFullCourse,
  currentLocale: PreferredLocale,
  updatedDescription: string,
  userId: string
): IClientPatch<IPatchAddAction<ICourseLocalizedText>> {
  const nextId = course.state.meta.mLDescription.nextId;
  return {
    ...createPatchBase(userId, course.versionId + 1),
    patch: {
      action: {
        at: nextId,
        item: {
          id: nextId,
          locale: currentLocale,
          value: {
            text: updatedDescription,
          },
        },
        type: PatchType.add,
      },
      position: `meta.mLDescription`,
    },
  };
}

export function addCourseTitlePatch(
  course: IFullCourse,
  userId: string,
  locale: PreferredLocale,
  title: string
): IClientPatch<IPatchAddAction<ICourseLocalizedText>> {
  const nextId = fullCourseViews.nextTitleId(course) as number;

  return {
    ...createPatchBase(userId, course.versionId + 1),
    patch: {
      action: {
        at: nextId,
        item: {
          id: nextId,
          locale,
          value: {
            text: title,
          },
        },
        type: PatchType.add,
      },
      position: `meta.mLTitle`,
    },
  };
}

export function modifyComponentTitlePatch(
  course: IFullCourse,
  unitPath: CourseUnitView[],
  component: ICourseComponent,
  userId: string,
  locale: PreferredLocale,
  title: string
): IClientPatch<IPatchSubstAction<string>> {
  const position = findTitlePosition(course, locale, component, unitPath);
  const currentTitle = courseComponentViews.localizedTitle(locale)(component) as string;

  return {
    ...createPatchBase(userId, course.versionId + 1),
    patch: substPatch(currentTitle, title, position),
  };
}

export function modifyCourseContentSettingsPatch(
  course: IFullCourse,
  unitPath: CourseUnitView[],
  settings: ICourseContentSettings,
  userId: string
): Array<IClientPatch<IPatchSubstAction<boolean>>> {
  const position = findComponent(course, unitPath);
  const oldSettings = fullCourseViews.assessmentSettings(course, unitPath);

  if (R.isNil(oldSettings) || R.isNil(settings)) {
    throw new Error("Invalid course content settings");
  }

  const patches = [];

  if (oldSettings.doPostAssessment !== settings.doPostAssessment) {
    patches.push({
      ...createPatchBase(userId, course.versionId + 1),
      patch: substPatch(
        oldSettings.doPostAssessment,
        settings.doPostAssessment,
        `${position}.settings.doPostAssessment`
      ),
    });
  }

  if (oldSettings.doPreAssessment !== settings.doPreAssessment) {
    patches.push({
      ...createPatchBase(userId, course.versionId + 1),
      patch: substPatch(oldSettings.doPreAssessment, settings.doPreAssessment, `${position}.settings.doPreAssessment`),
    });
  }

  return patches;
}

export function modifyCourseTitlePatch(
  course: IFullCourse,
  userId: string,
  locale: PreferredLocale,
  title: string
): IClientPatch<IPatchSubstAction<string>> {
  const position = findTitlePosition(course, locale);
  const currentTitle = fullCourseViews.localizedTitle(course, locale) as string;

  return {
    ...createPatchBase(userId, course.versionId + 1),
    patch: {
      action: {
        type: PatchType.subst,
        x: currentTitle,
        y: title,
      },
      position,
    },
  };
}

export function modifyAssessmentShowCorrectPatch(
  course: IFullCourse,
  unitPath: CourseUnitView[],
  showCorrect: boolean,
  userId: string
): IClientPatch<IPatchSubstAction<boolean>> {
  const position = findComponent(course, unitPath);
  return {
    ...createPatchBase(userId, course.versionId + 1),
    patch: substPatch(!showCorrect, showCorrect, `${position}.settings.showCorrectAnswersAfterAssessment`),
  };
}

export function modifyAssessmentSettingPatch<T extends IAssessment[keyof IAssessment]>(
  course: IFullCourse,
  unitPath: CourseUnitView[],
  userId: string,
  setting: keyof IAssessment,
  value: T
): IClientPatch<IPatchSubstAction<T>> {
  const assessment = fullCourseViews.assessment(course, unitPath);

  if (R.isNil(assessment)) {
    throw new Error(`Could not find target assessment in course ${course.versionId}`);
  }

  const position = `${findComponent(course, unitPath)}.test.${setting}`;

  return {
    ...createPatchBase(userId, course.versionId + 1),
    patch: substPatch(assessment[setting] as T, value, position),
  };
}

export function addChoiceExerciseItemPatch(
  course: IFullCourse,
  unitPath: CourseUnitView[],
  instructionSlide: Nullable<ICourseInstruction | IAssessment>,
  part: ICourseInstructionContentPart,
  userId: string
): IClientPatch<IPatchAddAction<IChoiceExerciseItem>> {
  const at = courseInstructionPartViews.choiceExercisePart.nextItemId(part as IMultipleChoiceExercise);
  let position: string;
  if (isAssessment(instructionSlide)) {
    position = `${findComponent(course, unitPath)}.test.questionBank.${part.id}.items`;
  } else {
    position = `${findComponent(course, unitPath, instructionSlide)}.parts.${part.id}.items`;
  }

  if (R.isNil(at)) {
    throw new Error(`Could not find nextId for choice item at ${position}`);
  }

  const item: IChoiceExerciseItem = {
    correct: false,
    hint: emptySet,
    id: at,
    text: emptySet,
  };

  return {
    ...createPatchBase(userId, course.versionId + 1),
    patch: addPatch(item, at, position),
  };
}

export function addClozeGapPatch(
  course: IFullCourse,
  unitPath: CourseUnitView[],
  instructionSlide: Nullable<ICourseInstruction | IAssessment>,
  part: ICourseInstructionContentPart,
  count: number,
  userId: string
): Array<IClientPatch<IPatchAddAction<IClozeGap>>> {
  const at = courseInstructionPartViews.clozeExercisePart.nextGapId(part as IClozeExercise);
  let position: string;
  if (isAssessment(instructionSlide)) {
    position = `${findComponent(course, unitPath)}.test.questionBank.${part.id}.gaps`;
  } else {
    position = `${findComponent(course, unitPath, instructionSlide)}.parts.${part.id}.gaps`;
  }

  if (R.isNil(at)) {
    throw new Error(`Could not find nextId for choice item at ${position}`);
  }

  const item = {
    answers: emptySet,
    hint: emptySet,
  };

  const patches = [];
  for (let i = at; i < at + count; i++) {
    patches.push({
      ...createPatchBase(userId, course.versionId + 1),
      patch: addPatch({ ...item, id: i }, i, position),
    });
  }

  return patches;
}

export function deleteExerciseItemPatch(
  course: IFullCourse,
  unitPath: CourseUnitView[],
  instructionSlide: ICourseInstruction | IAssessment,
  partId: number,
  choiceId: number,
  userId: string
): IClientPatch<IPatchSubstAction<boolean>> {
  let position: string;
  if (isAssessment(instructionSlide)) {
    position = `${findComponent(course, unitPath)}.test.questionBank.${partId}.items.${choiceId}.deleted`;
  } else {
    position = `${findComponent(course, unitPath, instructionSlide)}.parts.${partId}.items.${choiceId}.deleted`;
  }

  return {
    ...createPatchBase(userId, course.versionId + 1),
    patch: substPatch(false, true, position),
  };
}

export function deleteClozeGapPatch(
  course: IFullCourse,
  unitPath: CourseUnitView[],
  instructionSlide: ICourseInstruction | IAssessment,
  partId: number,
  gapId: number,
  userId: string
): IClientPatch<IPatchSubstAction<boolean>> {
  let position: string;
  if (isAssessment(instructionSlide)) {
    position = `${findComponent(course, unitPath)}.test.questionBank.${partId}.gaps.${gapId}.deleted`;
  } else {
    position = `${findComponent(course, unitPath, instructionSlide)}.parts.${partId}.gaps.${gapId}.deleted`;
  }

  return {
    ...createPatchBase(userId, course.versionId + 1),
    patch: substPatch(false, true, position),
  };
}

export function modifyClozeGapAnswersPatch(
  course: IFullCourse,
  unitPath: CourseUnitView[],
  instructionSlide: ICourseInstruction | IAssessment,
  part: ICourseInstructionContentPart,
  gapId: number,
  answerId: number,
  oldAnswers: string[],
  newAnswers: string[],
  currentLocale: PreferredLocale,
  userId: string
): IClientPatch<IPatchAddAction<IClozeGapLocalizedAnswer> | IPatchSubstAction<string[]>> {
  if (answerId < 0) {
    const at = courseInstructionPartViews.clozeExercisePart.nextGapAnswerId(part as IClozeExercise, gapId);

    if (R.isNil(at)) {
      throw new Error(`Could not find next gap answer id for gap ${gapId}`);
    }

    const answer: IClozeGapLocalizedAnswer = {
      id: at,
      locale: currentLocale,
      value: {
        value: newAnswers,
      },
    };

    const addPosition: string = isAssessment(instructionSlide)
      ? `${findComponent(course, unitPath)}.test.questionBank.${part.id}.gaps.${gapId}.answers`
      : `${findComponent(course, unitPath, instructionSlide)}.parts.${part.id}.gaps.${gapId}.answers`;

    return {
      ...createPatchBase(userId, course.versionId + 1),
      patch: addPatch(answer, at, addPosition),
    };
  }

  const position: string = isAssessment(instructionSlide)
    ? `${findComponent(course, unitPath)}.test.questionBank.${part.id}.gaps.${gapId}.answers.${answerId}.value.value`
    : `${findComponent(course, unitPath, instructionSlide)}.parts.${
        part.id
      }.gaps.${gapId}.answers.${answerId}.value.value`;

  return {
    ...createPatchBase(userId, course.versionId + 1),
    patch: substPatch(oldAnswers, newAnswers, position),
  };
}

export function modifyClozeGapHintPatch(
  course: IFullCourse,
  unitPath: CourseUnitView[],
  instructionSlide: ICourseInstruction | IAssessment,
  part: ICourseInstructionContentPart,
  gapId: number,
  hintId: number,
  oldHint: string,
  newHint: string,
  currentLocale: PreferredLocale,
  userId: string
): IClientPatch<IPatchAddAction<ICourseLocalizedText> | IPatchSubstAction<string>> {
  if (hintId < 0) {
    const at = courseInstructionPartViews.clozeExercisePart.nextGapHintId(part as IClozeExercise, gapId);

    if (R.isNil(at)) {
      throw new Error(`Could not find next gap hint id for gap ${gapId}`);
    }

    const hint: ICourseLocalizedText = {
      id: at,
      locale: currentLocale,
      value: {
        text: newHint,
      },
    };

    const addPosition: string = isAssessment(instructionSlide)
      ? `${findComponent(course, unitPath)}.test.questionBank.${part.id}.gaps.${gapId}.hint`
      : `${findComponent(course, unitPath, instructionSlide)}.parts.${part.id}.gaps.${gapId}.hint`;

    return {
      ...createPatchBase(userId, course.versionId + 1),
      patch: addPatch(hint, at, addPosition),
    };
  }

  const position: string = isAssessment(instructionSlide)
    ? `${findComponent(course, unitPath)}.test.questionBank.${part.id}.gaps.${gapId}.hint.${hintId}.value.text`
    : `${findComponent(course, unitPath, instructionSlide)}.parts.${part.id}.gaps.${gapId}.hint.${hintId}.value.text`;

  return {
    ...createPatchBase(userId, course.versionId + 1),
    patch: substPatch(oldHint, newHint, position),
  };
}

export function modifyChoiceExerciseItemCorrectPatch(
  course: IFullCourse,
  unitPath: CourseUnitView[],
  slide: ICourseInstruction | IAssessment,
  part: ICourseInstructionContentPart,
  choiceId: number,
  userId: string
): IClientPatch<IPatchSubstAction<boolean>> {
  let position: string;
  if (isAssessment(slide)) {
    position = `${findComponent(course, unitPath)}.test.questionBank.${part.id}.items.${choiceId}.correct`;
  } else {
    position = `${findComponent(course, unitPath, slide)}.parts.${part.id}.items.${choiceId}.correct`;
  }

  const currentValue = R.find(choice => choice.id === choiceId, (part as IMultipleChoiceExercise).content.items.elems);

  if (R.isNil(currentValue)) {
    throw new Error(`Could not find item ${choiceId} in ${position}`);
  }

  return {
    ...createPatchBase(userId, course.versionId + 1),
    patch: substPatch(!!currentValue.correct, !currentValue.correct, position),
  };
}

export function modifyExerciseItemHintPatch(
  course: IFullCourse,
  currentLocale: PreferredLocale,
  unitPath: CourseUnitView[],
  slide: ICourseInstruction | IAssessment,
  part: ICourseInstructionContentPart,
  choiceId: number,
  text: string,
  userId: string
): IClientPatch<IPatchSubstAction<string> | IPatchAddAction<ICourseLocalizedText>> {
  let choicePosition: string;
  if (isAssessment(slide)) {
    choicePosition = `${findComponent(course, unitPath)}.test.questionBank.${part.id}.items.${choiceId}`;
  } else {
    choicePosition = `${findComponent(course, unitPath, slide)}.parts.${part.id}.items.${choiceId}`;
  }

  const choice = R.find(item => item.id === choiceId, (part as IMultipleChoiceExercise).content.items.elems);

  if (R.isNil(choice)) {
    throw new Error(`Could not find choice ${choicePosition}`);
  }

  const currentHint = R.find(hint => hint.locale === currentLocale, choice.hint.elems);

  if (R.isNil(currentHint)) {
    const at = choice.hint.nextId;
    const item: ICourseLocalizedText = {
      id: at,
      locale: currentLocale,
      value: {
        text,
      },
    };
    return {
      ...createPatchBase(userId, course.versionId + 1),
      patch: addPatch(item, at, `${choicePosition}.hint`),
    };
  }

  return {
    ...createPatchBase(userId, course.versionId + 1),
    patch: substPatch(currentHint.value.text, text, `${choicePosition}.hint.${currentHint.id}.value.text`),
  };
}

export function modifyChoiceExerciseItemTextPatch(
  course: IFullCourse,
  currentLocale: PreferredLocale,
  unitPath: CourseUnitView[],
  slide: ICourseInstruction | IAssessment,
  part: ICourseInstructionContentPart,
  choiceId: number,
  text: string,
  userId: string
): IClientPatch<IPatchSubstAction<string> | IPatchAddAction<ICourseLocalizedText>> {
  let choicePosition: string;
  if (isAssessment(slide)) {
    choicePosition = `${findComponent(course, unitPath)}.test.questionBank.${part.id}.items.${choiceId}`;
  } else {
    choicePosition = `${findComponent(course, unitPath, slide)}.parts.${part.id}.items.${choiceId}`;
  }

  const choice = R.find(item => item.id === choiceId, (part as IMultipleChoiceExercise).content.items.elems);

  if (R.isNil(choice)) {
    throw new Error(`Could not find choice ${choicePosition}`);
  }

  const currentText = R.find(hint => hint.locale === currentLocale, choice.text.elems);

  if (R.isNil(currentText)) {
    const at = choice.text.nextId;
    const item: ICourseLocalizedText = {
      id: at,
      locale: currentLocale,
      value: {
        text,
      },
    };
    return {
      ...createPatchBase(userId, course.versionId + 1),
      patch: addPatch(item, at, `${choicePosition}.text`),
    };
  }

  return {
    ...createPatchBase(userId, course.versionId + 1),
    patch: substPatch(currentText.value.text, text, `${choicePosition}.text.${currentText.id}.value.text`),
  };
}

export function modifyChoiceExerciseWrapperContentPatch(
  wrapperKey: ChoiceExerciseWrapping,
  course: IFullCourse,
  currentLocale: PreferredLocale,
  unitPath: CourseUnitView[],
  slide: ICourseInstruction | IAssessment,
  part: IChoiceExercise,
  text: string,
  userId: string
): IClientPatch<IPatchSubstAction<string> | IPatchAddAction<ICourseLocalizedText>> {
  let position: string;
  if (isAssessment(slide)) {
    position = `${findComponent(course, unitPath)}.test.questionBank.${part.id}.wrapping.${wrapperKey}`;
  } else {
    position = `${findComponent(course, unitPath, slide)}.parts.${part.id}.wrapping.${wrapperKey}`;
  }
  const wrappingItem = part.content.wrapping[wrapperKey];
  const idx = R.findIndex(t => t.locale === currentLocale, wrappingItem.elems);

  if (idx < 0) {
    const at = wrappingItem.nextId;
    const item: ICourseLocalizedText = {
      id: at,
      locale: currentLocale,
      value: {
        text,
      },
    };
    return {
      ...createPatchBase(userId, course.versionId + 1),
      patch: addPatch(item, at, position),
    };
  }

  const currentValue = wrappingItem.elems[idx].value.text;

  return {
    ...createPatchBase(userId, course.versionId + 1),
    patch: substPatch(currentValue, text, `${position}.${wrappingItem.elems[idx].id}.value.text`),
  };
}

export function modifyChoiceExerciseRandomPatch(
  course: IFullCourse,
  unitPath: CourseUnitView[],
  slide: ICourseInstruction | IAssessment,
  part: ISingleChoiceExercise | IMultipleChoiceExercise,
  userId: string
): IClientPatch<IPatchSubstAction<boolean>> {
  let position: string;
  if (isAssessment(slide)) {
    position = `${findComponent(course, unitPath)}.test.questionBank.${part.id}.randomize`;
  } else {
    position = `${findComponent(course, unitPath, slide)}.parts.${part.id}.randomize`;
  }
  const isRandom = part.content.randomize;

  return {
    ...createPatchBase(userId, course.versionId + 1),
    patch: substPatch(isRandom, !isRandom, position),
  };
}

export function modifyClozeTemplatePatch(
  course: IFullCourse,
  unitPath: CourseUnitView[],
  slide: ICourseInstruction | IAssessment,
  clozePart: IClozeExercise,
  template: string,
  currentLocale: PreferredLocale,
  userId: string
): IClientPatch<IPatchAddAction<ICourseLocalizedText> | IPatchSubstAction<string>> {
  const idx = R.findIndex(t => t.locale === currentLocale, clozePart.content.template.elems);
  const localizedTemplate = clozePart.content.template.elems[idx];

  if (R.isNil(localizedTemplate)) {
    const at = clozePart.content.template.nextId;
    const newTemplate: ICourseLocalizedText = {
      id: at,
      locale: currentLocale,
      value: {
        text: template,
      },
    };

    let addPosition: string;
    if (isAssessment(slide)) {
      addPosition = `${findComponent(course, unitPath)}.test.questionBank.${clozePart.id}.template`;
    } else {
      addPosition = `${findComponent(course, unitPath, slide)}.parts.${clozePart.id}.template`;
    }

    return {
      ...createPatchBase(userId, course.versionId + 1),
      patch: addPatch(newTemplate, at, addPosition),
    };
  }

  const position: string = isAssessment(slide)
    ? `${findComponent(course, unitPath)}.test.questionBank.${clozePart.id}.template.${localizedTemplate.id}.value.text`
    : `${findComponent(course, unitPath, slide)}.parts.${clozePart.id}.template.${localizedTemplate.id}.value.text`;

  return {
    ...createPatchBase(userId, course.versionId + 1),
    patch: substPatch(localizedTemplate.value.text, template, position),
  };
}

export function deleteComponentPatch(
  course: IFullCourse,
  unitPath: CourseUnitView[],
  component: ICourseComponent,
  userId: string
): IClientPatch<IPatchSubstAction<boolean>> {
  const position = findComponent(course, unitPath, component);
  return {
    ...createPatchBase(userId, course.versionId + 1),
    patch: substPatch(false, true, `${position}.deleted`),
  };
}

export function addComponentPatch(
  course: IFullCourse,
  unitPath: CourseUnitView[],
  userId: string,
  tag: CourseComponentTag,
  type?: CourseInstructionType
): IClientPatch<IPatchAddAction<ICourseComponent>> | null {
  const position = `${findComponent(course, unitPath)}.children`;
  const at = findNextId(course, unitPath);

  if (R.isNil(at)) {
    return null;
  }

  const item =
    tag === CourseComponentTag.Unit
      ? createNewUnitItem(at)
      : createNewInstructionItem(at, type as CourseInstructionType);
  return {
    ...createPatchBase(userId, course.versionId + 1),
    patch: addPatch(item, at, position),
  };
}

export function changeInstructionTypePatch(
  course: IFullCourse,
  unitPath: CourseUnitView[],
  item: ICourseInstruction,
  type: CourseInstructionType,
  userId: string
): IClientPatch<IPatchSubstAction<CourseInstructionType>> {
  const position = findComponent(course, unitPath, item);

  return {
    ...createPatchBase(userId, course.versionId + 1),
    patch: substPatch(item.content.type, type, `${position}.type`),
  };
}

export function insertElementPatch<T>(
  course: IFullCourse,
  userId: string,
  element: T,
  position: string
): IClientPatch<IPatchInsertAction<T>> {
  return {
    ...createPatchBase(userId, course.versionId + 1),
    patch: {
      action: {
        elem: element,
        type: PatchType.ins,
      },
      position,
    },
  };
}

export function deleteElementPatch<T>(
  course: IFullCourse,
  userId: string,
  element: T,
  position: string
): IClientPatch<IPatchDeleteAction<T>> {
  return {
    ...createPatchBase(userId, course.versionId + 1),
    patch: {
      action: {
        elem: element,
        type: PatchType.del,
      },
      position,
    },
  };
}

export function modifyCourseDescriptionPatch(
  course: IFullCourse,
  currentLocale: PreferredLocale,
  updatedDescription: string,
  userId: string
): IClientPatch<IPatchSubstAction<string>> {
  const idx = R.findIndex(
    (a: ICourseLocalizedText) => a.locale === currentLocale,
    course.state.meta.mLDescription.elems
  );
  const currentDescription = fullCourseViews.localizedDescription(course, currentLocale);

  if (R.isNil(currentDescription)) {
    throw new Error("Course does not have current description");
  }

  return {
    ...createPatchBase(userId, course.versionId + 1),
    patch: substPatch(currentDescription, updatedDescription, `meta.mLDescription.${idx}.value.text`),
  };
}

export function modifyCourseLinearityPatch(
  course: IFullCourse,
  userId: string
): IClientPatch<IPatchSubstAction<boolean>> {
  const currentSettings = fullCourseViews.settings(course);

  if (R.isNil(currentSettings)) {
    throw new Error("Cannot find current course settings");
  }

  const { linear } = currentSettings;

  return {
    ...createPatchBase(userId, course.versionId + 1),
    patch: substPatch(linear, !linear, "cc.settings.linear"),
  };
}

export function modifyMasterLocalePatch(
  course: IFullCourse,
  locale: PreferredLocale,
  userId: string
): IClientPatch<IPatchSubstAction<PreferredLocale>> {
  const currentLocale = fullCourseViews.masterLocale(course);

  if (R.isNil(currentLocale)) {
    throw new Error("Could not find current locale");
  }

  return {
    ...createPatchBase(userId, course.versionId + 1),
    patch: substPatch(currentLocale, locale, "localeSettings.masterLocale"),
  };
}

export function modifyCustomCompletedMessagePatch(
  course: IFullCourse,
  message: string,
  locale: PreferredLocale,
  userId: string
): IClientPatch<IPatchAddAction<ICourseLocalizedText> | IPatchSubstAction<string>> {
  const currentSettings = fullCourseViews.settings(course);

  if (R.isNil(currentSettings)) {
    throw new Error("Could not find course settings");
  }

  const basePatch = createPatchBase(userId, course.versionId + 1);
  const existingMessage = fullCourseViews.customCompletedMessage(course, locale);

  if (!R.isNil(existingMessage)) {
    const idx = R.findIndex(m => m.locale === locale, currentSettings.customCompletedMessage.elems);
    return {
      ...basePatch,
      patch: substPatch(existingMessage, message, `cc.settings.customCompletedMessage.${idx}.value.text`),
    };
  }

  const at = fullCourseViews.nextCustomCompletedMessageId(course) as number;
  const item = {
    id: at,
    locale,
    value: {
      text: message,
    },
  } as ICourseLocalizedText;
  return {
    ...basePatch,
    patch: addPatch(item, at, `cc.settings.customCompletedMessage`),
  };
}

export function modifyUseCustomCompletedMessagePatch(
  course: IFullCourse,
  userId: string
): IClientPatch<IPatchSubstAction<boolean>> {
  const currentSettings = fullCourseViews.settings(course);

  if (R.isNil(currentSettings)) {
    throw new Error("Cannot find current course settings");
  }

  const { useCustomCompletedMessage } = currentSettings;

  return {
    ...createPatchBase(userId, course.versionId + 1),
    patch: substPatch(useCustomCompletedMessage, !useCustomCompletedMessage, "cc.settings.useCustomCompletedMessage"),
  };
}

export function modifyCoursePlayerPatch(
  course: IFullCourse,
  coursePlayer: CoursePlayer,
  userId: string
): IClientPatch<IPatchSubstAction<CoursePlayer>> {
  return {
    ...createPatchBase(userId, course.versionId + 1),
    patch: substPatch(course.state.coursePlayer, coursePlayer, "coursePlayer"),
  };
}

export function modifyReadSpeakerPatch(course: IFullCourse, userId: string): IClientPatch<IPatchSubstAction<boolean>> {
  const {
    state: { readSpeaker },
  } = course;
  return {
    ...createPatchBase(userId, course.versionId + 1),
    patch: substPatch(readSpeaker, !readSpeaker, "readSpeaker"),
  };
}

export function modifyCourseDeletedPatch(
  course: IFullCourse,
  userId: string
): IClientPatch<IPatchSubstAction<boolean>> {
  const {
    state: { deleted },
  } = course;
  return {
    ...createPatchBase(userId, course.versionId + 1),
    patch: substPatch(deleted, !deleted, "deleted"),
  };
}

export function modifyCourseStylePatch(
  userId: string,
  course: IFullCourse,
  newStyleId: string
): IClientPatch<IPatchSubstAction<string | null>> {
  const oldStyleId = fullCourseViews.style(course);
  return {
    ...createPatchBase(userId, course.versionId + 1),
    patch: substPatch(oldStyleId, newStyleId, "style"),
  };
}

export function modifyOrganizationOwner(
  userId: string,
  course: IFullCourse,
  newOrganizationId: string
): IClientPatch<IPatchSubstAction<string | null>> {
  return {
    ...createPatchBase(userId, course.versionId + 1),
    patch: substPatch(course.state.owner, newOrganizationId, "owner"),
  };
}

export function modifylogoImagePatch(
  userId: string,
  style: ICourseStyle,
  imageId: string | null
): IClientPatch<IPatchSubstAction<string | null>> {
  return {
    ...createPatchBase(userId, style.versionId + 1),
    patch: substPatch(style.state.logoImageId, imageId, "logoImageId"),
  };
}

export function modifyLessCodePatch(
  userId: string,
  style: ICourseStyle,
  lessCode: string
): IClientPatch<IPatchSubstAction<string>> {
  return {
    ...createPatchBase(userId, style.versionId + 1),
    patch: substPatch(style.state.lessCode, lessCode, "lessCode"),
  };
}

export function modifyBackgroundPatch(
  userId: string,
  course: IFullCourse,
  backgroundUrl: string | null
): IClientPatch<IPatchSubstAction<string | null>> {
  return {
    ...createPatchBase(userId, course.versionId + 1),
    patch: substPatch(course.state.background, backgroundUrl, "background"),
  };
}

export function createPatchBase(createdBy: string, versionId: number) {
  const createdAt = new Date().toISOString();
  return {
    createdAt,
    createdBy,
    undoVersionId: null,
    versionId,
  };
}

const emptySet: ICourseSet<never> = {
  elems: [],
  nextId: 0,
};

const emptyWrapping: ICourseExerciseWrapping = {
  rebuke: emptySet,
  reinforcement: emptySet,
  taskAfter: emptySet,
  taskBefore: emptySet,
  title: emptySet,
};

function createNewUnitItem(id: number): ICourseUnit {
  return {
    content: {
      children: emptySet,
      settings: {
        doPostAssessment: true,
        doPreAssessment: true,
        showCorrectAnswersAfterAssessment: true,
      },
      test: {
        accumulateAnswers: true,
        earlyExit: true,
        isRandomized: true,
        limitAllowedMistakes: true,
        numAllowedMistakes: 0,
        numCorrectAnswersRequired: 1,
        questionBank: emptySet,
        questionDrawingPreference: QuestionDrawingPreference.PreferNewOrWrongQuestions,
      },
      title: emptySet,
    },
    id,
    tag: CourseComponentTag.Unit,
  };
}

function createNewInstructionItem(id: number, type: CourseInstructionType): ICourseInstruction {
  return {
    content: {
      name: emptySet,
      pageLockSettings: null,
      parts: emptySet,
      pseudoLesson: false,
      type,
    },
    id,
    tag: CourseComponentTag.Instruction,
  };
}

export function createNewInstructionPart(
  tag: CourseComponentType,
  id: number,
  properties?: object
): ICourseInstructionContentPart {
  switch (tag) {
    case CourseComponentType.Teaching:
      const teachingItem: ITeachingContent = {
        content: {
          background: { imageId: null },
          markdown: emptySet,
          voiceOver: emptySet,
          voiceOverText: emptySet,
        },
        id,
        sort: 0,
        tag,
      };
      return R.mergeDeepRight(teachingItem, properties);

    case CourseComponentType.Spacer:
      const spacerItem: ISpacerPart = {
        content: {
          settings: {
            color: colors.white,
            height: DEFAULT_MARGIN,
          },
        },
        id,
        sort: 0,
        tag,
      };
      return R.mergeDeepRight(spacerItem, properties);

    case CourseComponentType.Image:
      const imageItem: IImagePart = {
        content: {
          settings: {
            align: "center",
            altText: "",
            fileImageId: "",
            isOpenInNewWindow: false,
            link: "",
          },
        },
        id,
        sort: 0,
        tag,
      };
      return R.mergeDeepRight(imageItem, properties);
    case CourseComponentType.Document:
      const documentItem: IDocumentPart = {
        content: {
          settings: {
            ...DEFAULT_DOCUMENT_SETTINGS,
          },
        },
        id,
        sort: 0,
        tag,
      };
      return R.mergeDeepRight(documentItem, properties);
    case CourseComponentType.Divider:
      const dividerItem: IDividerPart = {
        content: {
          settings: {
            color: colors.black,
            marginBottom: DEFAULT_MARGIN,
            marginTop: DEFAULT_MARGIN,
            style: "solid",
            thickness: 1,
          },
        },
        id,
        sort: 0,
        tag,
      };
      return R.mergeDeepRight(dividerItem, properties);

    case CourseComponentType.Choice:
      const choiceItem: IChoiceExercise = {
        content: {
          items: {
            elems: [
              {
                correct: false,
                hint: emptySet,
                id: 0,
                text: emptySet,
              },
              {
                correct: false,
                hint: emptySet,
                id: 1,
                text: emptySet,
              },
            ],
            nextId: 2,
          },
          randomize: true,
          singleChoice: false,
          wrapping: emptyWrapping,
        },
        id,
        sort: 0,
        tag,
      };
      return R.mergeDeepRight(choiceItem, properties);

    case CourseComponentType.Cloze:
      const clozeItem: IClozeExercise = {
        content: {
          gaps: emptySet,
          template: emptySet,
          wrapping: emptyWrapping,
        },
        id,
        sort: 0,
        tag,
      };
      return R.mergeDeepRight(clozeItem, properties);

    case CourseComponentType.UnorderedList:
      const unorderedListItem: IUnorderedListExercise = {
        content: {
          items: emptySet,
          wrapping: emptyWrapping,
        },
        id,
        sort: 0,
        tag,
      };
      return R.mergeDeepRight(unorderedListItem, properties);

    case CourseComponentType.ListWithoutOptions:
      const listWithoutItems: IListWithoutOptionsExercise = {
        content: {
          hintStyle: "ByItem",
          items: emptySet,
          wrapping: emptyWrapping,
        },
        id,
        sort: 0,
        tag,
      };
      return listWithoutItems;
    case CourseComponentType.ListWithOptions:
      const listWithOptions: IListWithOptionsExercise = {
        content: {
          hintStyle: "ByItem",
          items: emptySet,
          wrapping: emptyWrapping,
        },
        id,
        sort: 0,
        tag,
      };
      return listWithOptions;
    case CourseComponentType.Grid:
      const grid: IGridPart = {
        content: {
          background: { imageId: null },
          columns: {
            content: [null, null],
            tag: "TwoColumns",
          },
          layout: {
            content: [1, 1],
            tag: "TwoColumns",
          },
        },
        id,
        sort: 0,
        tag,
      };
      return R.mergeDeepRight(grid, properties);

    default:
      throw new Error("Unkown CourseComponentType");
  }
}

export const togglePseudoLessonPatch = (
  accountId: string,
  versionId: number,
  unitPath: CourseUnitView[],
  course: IFullCourse,
  component: ICourseInstruction,
  checked: boolean
): IClientPatch<IPatchSubstAction<boolean>> => {
  const position = findComponent(course, unitPath, component);
  const patch = {
    ...createPatchBase(accountId, versionId + 1),
    patch: substPatch(!checked, checked, `${position}.pseudoLesson`),
  };
  return patch;
};

export function createNewStyle(courseId: string, activeOrg: string): ICourseStyle {
  return {
    state: {
      assets: {
        elems: [],
        nextId: 0,
      },
      bodyFontFamilyId: null,
      course: courseId,
      deleted: false,
      displayName: `Style for Course [DO NOT EDIT]
        crs.${courseId}`,
      headerFontFamilyId: null,
      lessCode: "",
      logo: null,
      logoImageId: null,
      monospaceFontFamilyId: null,
      owner: activeOrg,
    },
    versionId: 0,
  };
}

export const modifyObjectDeletedPatch = (
  versionId: number,
  accountId: string
): IClientPatch<IPatchSubstAction<boolean>> => ({
  ...createPatchBase(accountId, versionId + 1),
  patch: {
    action: {
      type: PatchType.subst,
      x: false,
      y: true,
    },
    position: "deleted",
  },
});

export function modifyPageLockSettingsPatch(
  course: IFullCourse,
  userId: string,
  instructionSlide: ICourseInstruction,
  unitPath: CourseUnitView[],
  settings: IPageLockSettings
): IClientPatch<IPatchSubstAction<IPageLockSettings | null>> {
  const component = findComponent(course, unitPath, instructionSlide);
  const position = `${component}.pageLockSettings`;
  const currentSettings = courseInstructionViews.pageLockSettings(instructionSlide);

  return {
    ...createPatchBase(userId, course.versionId + 1),
    patch: substPatch(currentSettings, settings, position),
  };
}

export const createGridContentPatches = (
  course: IFullCourse,
  userId: string,
  comp: ICourseInstruction,
  path: CourseUnitView[],
  gridPatch: IClientPatch<IPatchAddAction<ICourseInstructionContentPart>>,
  childParts: Array<ICourseInstructionContentPart | null>,
  position: number | undefined
): Array<
  IClientPatch<IPatchAddAction<ICourseInstructionContentPart>> | IClientPatch<IPatchSubstAction<NullablePartPosition>>
> => {
  const [copyInstructionPatches, substInstructionPatches] = childParts.reduce(
    (
      [copyPatches, substPatches]: [
        Array<IClientPatch<IPatchAddAction<ICourseInstructionContentPart>>>,
        Array<IClientPatch<IPatchSubstAction<NullablePartPosition>>>
      ],
      childPart: ICourseInstructionContentPart,
      partIndex: number
    ) => {
      if (R.isNil(childPart)) {
        return [copyPatches, substPatches];
      }

      const id =
        partIndex === 0
          ? gridPatch.patch.action.item.id + 1
          : copyPatches[copyPatches.length - 1].patch.action.item.id + 1;

      const copyPartPatch = copyGridPartPatch(course, userId, comp, path, childPart, id, position);
      const substPartPatch = substLeafCourseInstructionColumnPatch(
        course,
        userId,
        comp,
        path,
        gridPatch.patch.action.item as IGridPart,
        partIndex,
        copyPartPatch.patch.action.at
      );

      return [
        [...copyPatches, copyPartPatch],
        [...substPatches, substPartPatch],
      ];
    },
    [[], []]
  );

  return [...copyInstructionPatches, ...substInstructionPatches];
};
